import { useCallback, useEffect, useRef, useState, type CSSProperties, type JSX } from 'react'
import { api, type AccountRanking, type EndUse, type RankedStrategy, type Strategy } from '../api.js'
import { AGGREGATE_KW_SIZE, IndicativeKw } from './IndicativeKw.js'
import { formatHour } from './Pill.js'
import { T } from '../tokens.js'

/**
 * The strategy surface (F-18, R-32, R-33, spec §7.6, `design-brief.md` §4.5).
 *
 * Ranked, sector-specific suggestions for *how* a customer might reach the
 * number — the question that decides enrollment once the estimate is accepted.
 * Everything on it is advisory, and the six design implications the brief calls
 * "unusually constraining" are each a rule this file keeps:
 *
 * 1. **The framing statement travels with the list**, at readable size, at the
 *    head, never collapsed behind a disclosure and never in 10px micro-type.
 *    It is the first thing under the heading because the tone failure is the
 *    expensive one: a utility that appears to be telling a hotel how to run its
 *    housekeeping has lost the meeting regardless of whether the kilowatts were
 *    right.
 * 2. **Every yield is labelled indicative and looks nothing like a metered
 *    figure.** See `IndicativeKw.tsx`, which also explains why it is not
 *    `KwFigure`.
 * 3. **Nothing here implies the yields add.** Overlapping strategies are
 *    declared on the row, and the only total on the surface is the engine's
 *    overlap-adjusted, capped `indicativeTotalKw`. This component never sums a
 *    column.
 * 4. **Prerequisites are stated as requirements, not as failures.** The field
 *    is `statedPrerequisites` and nothing in this system knows which of them
 *    the account meets — see `NEEDS_LABEL`.
 * 5. **The empty list is a designed state**, not a blank area and not an error.
 * 6. **Rejecting a strategy offers the end-use exclusion; it never applies
 *    it.** See `onReject`.
 *
 * ## Three states that must not be confused, and none is an error
 *
 * `strategies-empty` is *no strategies at all*, and it arrives two ways that
 * need **different words**:
 *
 * - `data-empty-reason="no-library"` — `strategyLibraryVersionId` is `null`, so
 *   no strategy library is in force on the estimate's date and NOTHING can be
 *   ranked for ANY account. Saying "none on file for this account's industry"
 *   here is a false statement about the account, made 313 times over, and it
 *   sends whoever debugs it hunting for a NAICS-prefix bug.
 * - `data-empty-reason="no-match"` — a library IS in force and none of its rows
 *   matched this account's NAICS prefix or building type. *That* is the
 *   statement about the industry, and it is true.
 *
 * `strategy-row` with `data-quantified="false"` is the **fallback path**: the
 * strategy applies to this sector but the account's load decomposition gives it
 * nothing to act on, so it is shown with the reason and no figure. 134 of the
 * 313 accounts the seeded run scored are entirely fallback — 42.8% — and a
 * surface that drew them as an empty list would tell those reps the account has
 * no options, which is a different and false statement.
 *
 * ## Structure is in `data-*`, and size is read off the inline style
 *
 * jsdom under vitest's `css: false` default answers `getComputedStyle(...)`
 * with `''` for anything without an inline style, so `parseFloat('') >= 13` is
 * false forever and `'' !== '44px'` is true forever. Facts about the payload —
 * a figure's indicativeness, whether a row is quantified, which of the two
 * empty states this is — are attributes set from `tokens.ts` and from the
 * payload. `AccountDetail.test.tsx:19-22` already ruled on this for the
 * package.
 *
 * **Type size is the exception, and deliberately not an attribute.** A
 * `data-size` set beside a `fontSize` is two expressions, and a test reading
 * the attribute passes while the rendering drops to 10px — measured, whole web
 * suite green. The framing carries a real *inline* style, so `element.style.
 * fontSize` is the rendered value itself and there is only one expression to
 * read. `NoteFeed.test.tsx:167-176` uses the same technique on the timestamp
 * gutter and says the same thing about `getComputedStyle`.
 */

/**
 * **R-33's mandated framing statement**, and the sentence this whole surface is
 * built around.
 *
 * The acceptance criterion: *"Every surface displaying strategies carries a
 * framing statement that these are starting points to help the customer
 * consider where curtailment is easiest to achieve with least effort and
 * disruption, and are not instructions, feasibility determinations, or
 * commitments."*
 *
 * The one-pager carries its own copy of this sentence
 * (`packages/server/src/http/routes/onepager.ts`, `STRATEGY_FRAMING`) because
 * the two packages cannot share a module — the same reason `formatKw` is
 * written twice.
 *
 * **Which test holds which copy.** Both copies used to be held only by
 * `toContain('starting points')`-shaped assertions, and appending *"Present
 * them to the customer as the recommended actions for this site."* to BOTH left
 * the entire suite green — 76 files, 1832 tests, exit 0 — which is the paper
 * document contradicting the screen with nothing to stop it. So:
 *
 * - `StrategyList.test.tsx`, "says what R-33 requires it to say" — asserts the
 *   *rendered* text of this constant equals the sentence, spelled out there as
 *   a literal. An edit to the words here reddens it.
 * - `onepager.test.ts`, "the screen's copy and the paper's copy are the same
 *   sentence" — reads BOTH source files off disk (no import, so no package
 *   boundary is crossed) and asserts each declaration yields that same literal.
 *   An edit to *either* copy, or an edit to both, reddens it.
 */
export const STRATEGY_FRAMING
  = 'These are starting points to help the customer consider where curtailment is easiest to '
  + 'achieve, with the least effort and disruption. They are not instructions, not feasibility '
  + 'determinations, and not commitments — the customer knows their operation and we do not.'

/**
 * The framing's type size, in px, and the **one** expression that sets it.
 *
 * Requirement 1 is "at readable size, not 10px micro-type". This used to be
 * rendered twice — once as `fontSize` and once as a `data-size` attribute the
 * test read — and the two could not be held together: changing `fontSize` to a
 * literal `10` while leaving `data-size={FRAMING_SIZE}` alone left the whole
 * web suite green. The attribute is gone; `StrategyList.test.tsx` reads
 * `framing.style.fontSize`, which is the rendered value and cannot be a
 * different expression from the one that renders it.
 */
export const FRAMING_SIZE = 15

/**
 * The empty state that IS about this account: a library is in force and nothing
 * in it matches this account's industry.
 *
 * Display rule 4's shape — plain grey text, no dashed box, no prompt. Paired
 * with `NO_STRATEGY_LIBRARY` below, which is the state this sentence must never
 * be used for.
 */
export const NO_STRATEGIES
  = 'No curtailment strategies are on file for this account’s industry. '
  + 'Some accounts have no good options, and saying so plainly beats a padded list.'

/**
 * The empty state that is **not** about this account, and the reason it may not
 * borrow the sentence above.
 *
 * `AccountRanking.strategyLibraryVersionId` is `null` when no strategy library
 * is in force on the estimate's date — the state every database in this
 * repository is in until the seed runs, and the state one deleted row puts a
 * production database into. `resolveStrategyLibraryAt` answers with an empty
 * library rather than throwing, so the route returns 200 with `ranked: []` for
 * *every* account.
 *
 * Rendering `NO_STRATEGIES` there tells 313 reps that their accounts'
 * industries have no options on file, when the truth is that the program has
 * committed no library at all. It is a false statement to a user, and it costs
 * whoever debugs it a hunt through NAICS-prefix matching for a bug that is not
 * there.
 */
export const NO_STRATEGY_LIBRARY
  = 'No strategy library is in force for this account’s estimate date, so no strategies can be '
  + 'ranked for any account yet. Nothing here is a finding about this account’s industry.'

export const STRATEGIES_FAILED = 'The strategy list for this account could not be loaded.'

/**
 * Stated as a **requirement**, never as a failure.
 *
 * `RankedStrategy.statedPrerequisites` is every prerequisite the strategy
 * declares. The ranking is handed no record of which of them the account
 * actually meets — there is no such input in `rankStrategies`' signature and no
 * engine source for one — so "Unmet: building management system" would be this
 * screen telling a rep the account HAS no BMS when nothing in the system knows
 * either way. "Needs: building management system" is a question the rep can
 * ask, which is what the brief asks for.
 */
export const NEEDS_LABEL = 'Needs'

/**
 * The reason recorded against a one-click rejection, and why there is no
 * free-text box here.
 *
 * `POST .../rejection` requires a non-empty reason (R-35: the record is what
 * makes accuracy reporting possible later), and the offer has to survive one
 * click — a modal between "the customer says no" and the what-if is a modal in
 * the middle of a conversation.
 *
 * A free-text reason field on a strategy row was the obvious alternative and is
 * deliberately not built: it is a note-shaped input on the one surface this
 * plan works hardest to keep note text out of, and `strategy_presentations`
 * would become a second, unsearchable, unredactable notes table. A reason
 * vocabulary — "not available", "too disruptive", "already doing it" — is a
 * later plan's job and wants a schema decision, not a text box.
 *
 * This string describes the act and claims nothing about the customer's words.
 */
export const REJECTION_REASON = 'Marked not available by the rep from the strategy list.'

/** What the offer says when the rejection has been recorded. Never applied here. */
export const OFFER_PREFIX = 'Offered as a what-if exclusion:'

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const sectionRule: CSSProperties = {
  borderTop: `1.5px solid ${T.color.rule}`,
  marginTop: 22,
  paddingTop: 16,
}

const quietButton: CSSProperties = {
  border: `1.5px solid ${T.color.border}`,
  background: '#fff',
  color: T.color.body,
  borderRadius: T.radius.button,
  fontFamily: T.fontFamily,
  fontSize: 11,
  fontWeight: T.weight.bold,
  letterSpacing: '0.07em',
  textTransform: T.micro.transform,
  padding: '7px 12px',
  cursor: 'pointer',
}

/**
 * Human words for the seven end uses, mid-sentence.
 *
 * Lower case and deliberately not `WhatIf.tsx`'s `END_USE_LABELS`, which are
 * title-cased because they label checkboxes. "withholding Cooling" in a
 * sentence a rep reads out is the kind of small wrongness that reads as a bug.
 */
const END_USE_WORDS: Record<EndUse, string> = {
  cooling: 'cooling',
  ventilation: 'ventilation',
  lighting: 'lighting',
  refrigeration: 'refrigeration',
  process: 'process load',
  plug: 'plug load',
  other: 'other load',
}

/** The same words, for the screen that renders the offer beside the panel. */
export const endUseWords = (endUse: EndUse): string => END_USE_WORDS[endUse]

/**
 * `timing` in words. The brief requires it on every strategy — "its timing
 * relative to the event (pre-event, in-window, or specific hours)" — and all
 * eight of its worked samples carry one.
 *
 * `hours` is half-open, `startHour` inclusive and `endHour` exclusive, so
 * `{ 15, 17 }` is "3:00pm – 5:00pm" and ends where the window begins.
 *
 * Exported so all three branches can be asserted directly. They were asserted
 * NOWHERE — no test contained "Before the window" or "In the window", and the
 * `strategy-timing` element below was rendered and never queried — which left a
 * brief requirement carried by a function nothing held. `onepager.ts` has the
 * paper twin of this function and its test now holds that one the same way.
 */
export function timingWords(timing: Strategy['timing']): string {
  if (timing.kind === 'pre_event') return 'Before the window'
  if (timing.kind === 'in_window') return 'In the window'
  return `${formatHour(timing.startHour)} – ${formatHour(timing.endHour)}`
}

const IMPACT_WORDS = { low: 'Low impact', medium: 'Medium impact', high: 'High impact' } as const

const IMPACT_STYLE: Record<'low' | 'medium' | 'high', CSSProperties> = {
  low: { background: T.tint.enrolled, color: T.color.enrolledInk },
  medium: { background: T.tint.stale, color: T.color.warnInk },
  high: { background: T.tint.red, color: T.color.declinedInk },
}

function ImpactPill({ impact }: { impact: 'low' | 'medium' | 'high' }): JSX.Element {
  return (
    <span
      data-testid="strategy-impact"
      data-impact={impact}
      style={{
        ...microLabel,
        ...IMPACT_STYLE[impact],
        borderRadius: T.radius.pill,
        padding: '3px 10px',
        whiteSpace: 'nowrap',
      }}
    >
      {IMPACT_WORDS[impact]}
    </span>
  )
}

/**
 * Which other strategies **on this list** act on the same load.
 *
 * The brief's rule is "overlapping strategies are declared", and the honest
 * declaration is per-pair rather than a general disclaimer: pre-cooling and a
 * setback both act on cooling, and a rep reading "these two do not add" beside
 * both of them can say so out loud. Computed over the rows actually shown, so
 * it never names a strategy the customer is not looking at.
 *
 * It is a *presentational* fact and the engine agrees: `aggregateStrategies`
 * adjusts for overlap by end use alone, and `Strategy.timing` is deliberately
 * not one of its inputs.
 */
function overlapsWith(entry: RankedStrategy, all: RankedStrategy[]): string[] {
  return all
    .filter((other) => other.strategy.id !== entry.strategy.id
      && other.notApplicable === null
      && other.strategy.endUses.some((endUse) => entry.strategy.endUses.includes(endUse)))
    .map((other) => other.strategy.name)
}

type Load =
  | { phase: 'loading' }
  | { phase: 'ready'; ranking: AccountRanking }
  | { phase: 'failed' }

/** A row's rejection, once recorded. `endUses` is what the server offered. */
interface Rejected {
  endUses: EndUse[]
  /** Set when the rejection could not be recorded, so the row can say so. */
  failed: boolean
}

export interface StrategyListProps {
  accountId: string
  /**
   * Whether to draw the aggregate at all. **Both layouts are permitted and
   * neither is a compromise.**
   *
   * `design-brief.md:183-184`: *"any aggregate shown must be overlap-adjusted
   * and capped at the metered estimate. The safest layout may show no total at
   * all."* Defaulted to `true` because a rep who is asked "so what does all
   * that come to" needs an answer that is not the sum the customer just did in
   * their head; set it to `false` on any surface where a total would be read as
   * a second nomination.
   */
  showAggregate?: boolean
  /**
   * Where a rejection's suggested exclusion goes (R-33), and the reason this is
   * optional.
   *
   * When it is absent no reject control is drawn at all. That is not a
   * simplification: `rejectStrategy` throws `StrategyNotPresentedError` for an
   * account with no current estimate, because a rep can only reject something
   * they were offered and the offer is the `strategy_presentations` row. A
   * button that could only ever produce a 404 is a control that does nothing.
   *
   * The caller **must not** apply the exclusion on receipt. It is an offer; the
   * rep applies it through the scenario path, where a scenario is visibly a
   * scenario.
   */
  onSuggestExclusion?: (endUses: EndUse[]) => void
}

export function StrategyList(
  { accountId, showAggregate = true, onSuggestExclusion }: StrategyListProps,
): JSX.Element {
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  const [rejected, setRejected] = useState<Record<string, Rejected>>({})
  const alive = useRef(true)
  /**
   * Which request the surface is waiting for. The account can change under a
   * mounted list, and the slower answer is not the answer to the name on the
   * page — the same guard `NoteFeed` and `AccountDetail` put on theirs.
   */
  const era = useRef(0)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    const mine = era.current + 1
    era.current = mine
    setLoad({ phase: 'loading' })
    // A rejection belongs to the account it was recorded against. Carrying one
    // across would put one account's offer on another account's row for as
    // long as the fetch took.
    setRejected({})
    void api.strategies(accountId)
      .then((ranking) => {
        if (alive.current && era.current === mine) setLoad({ phase: 'ready', ranking })
      })
      .catch(() => {
        if (alive.current && era.current === mine) setLoad({ phase: 'failed' })
      })
  }, [accountId])

  /**
   * **The offer, and the whole of R-33's interaction rule.**
   *
   * This records the rejection and hands the suggested exclusion to the caller.
   * It does not apply it, does not recompute anything and does not touch a
   * figure on this page. The route agrees — it persists no estimate — and Task
   * 9's negative-space suite asserts that rejecting every strategy on an
   * account leaves the stored estimate byte-identical.
   *
   * The suggestion may name an end use outside `WhatIf.tsx`'s three-checkbox
   * `EXCLUDABLE` shortlist: the scenarios route validates against all seven
   * `END_USES` and `computeProfile` honours all seven, so a rejected `process`
   * or `ventilation` strategy has a real, applicable exclusion. It is passed
   * on whole and never filtered here; the panel grows a checkbox for whatever
   * arrives (see `WhatIf.tsx`'s `optionsFor`).
   */
  const onReject = useCallback((strategyId: string): void => {
    void api.rejectStrategy(accountId, strategyId, REJECTION_REASON)
      .then(({ suggestedOverride }) => {
        if (!alive.current) return
        const endUses = suggestedOverride.excludedEndUses ?? []
        setRejected((previous) => ({ ...previous, [strategyId]: { endUses, failed: false } }))
        // An empty suggestion offers nothing, so nothing is offered. Every
        // strategy in the shipped library addresses at least one end use, but
        // the library is a document a program manager writes and this surface
        // does not get to assume its contents.
        if (endUses.length > 0) onSuggestExclusion?.(endUses)
      })
      .catch(() => {
        if (!alive.current) return
        setRejected((previous) => ({ ...previous, [strategyId]: { endUses: [], failed: true } }))
      })
  }, [accountId, onSuggestExclusion])

  return (
    <section style={sectionRule} data-testid="strategies" data-phase={load.phase}>
      <h2 style={{ ...microLabel, margin: 0, color: T.color.ink }}>Where the kilowatts might come from</h2>

      {/*
        Requirement 1. It is here — above the list, at 14px body type, in the
        normal flow — rather than under it in micro-type, because a framing
        statement the eye skips is the advisory line being crossed silently.
        `data-role` is what a test holds the placement with and `hidden` is what
        it can prove absent; the SIZE is held by reading `style.fontSize` back
        off this element, because that is the value that renders. A `data-size`
        attribute sat here once and proved nothing — see `FRAMING_SIZE`.
      */}
      <p
        data-testid="framing-statement"
        data-role="framing"
        style={{
          fontSize: FRAMING_SIZE,
          lineHeight: 1.55,
          color: T.color.body,
          margin: '10px 0 0',
          // The one measure kept on this surface. This is the longest body copy
          // here and the only paragraph that runs to several lines, so an
          // unbounded line length is a real readability cost rather than a
          // notional one. 86ch matches the widest measure the app already uses
          // (`AccountDetail.tsx`'s data-quality note); everything shorter than
          // this now fills its column.
          maxWidth: '86ch',
          borderLeft: `4px solid ${T.color.cyan}`,
          paddingLeft: 12,
        }}
      >
        {STRATEGY_FRAMING}
      </p>

      {load.phase === 'loading'
        ? <p style={{ fontSize: 13.5, color: T.color.micro, marginTop: 14 }}>Loading strategies…</p>
        : load.phase === 'failed'
          ? (
            // The one state on this surface that IS an error, and the only
            // `role="alert"` in the file. The empty list and the fallback path
            // are designed states and must never reach this branch.
            <p role="alert" data-testid="strategies-failed" style={{ fontSize: 13, color: T.color.red, marginTop: 14 }}>
              {STRATEGIES_FAILED}
            </p>
          )
          : (
            <Ranking
              ranking={load.ranking}
              showAggregate={showAggregate}
              rejected={rejected}
              onReject={onSuggestExclusion === undefined ? undefined : onReject}
            />
          )}
    </section>
  )
}

function Ranking(
  { ranking, showAggregate, rejected, onReject }: {
    ranking: AccountRanking
    showAggregate: boolean
    rejected: Record<string, Rejected>
    onReject?: ((strategyId: string) => void) | undefined
  },
): JSX.Element {
  const { ranked, aggregate, strategyLibraryVersionId } = ranking

  if (ranked.length === 0) {
    // TWO states reach here, and they are different statements. `null` is "no
    // library is in force" — true of every account at once, and nothing to do
    // with this one's industry. Anything else is "a library is in force and
    // none of it matched this account". See the constants for what each costs
    // when the wrong one is shown.
    const noLibrary = strategyLibraryVersionId === null
    return (
      // Requirement 5. Grey body text and nothing else — no dashed box, no
      // icon, no `role="alert"`. "Returning no strategies is a valid, designed
      // state" (`design-brief.md:194-195`).
      <p
        data-testid="strategies-empty"
        data-empty-reason={noLibrary ? 'no-library' : 'no-match'}
        style={{ fontSize: 14.5, lineHeight: 1.55, color: T.color.muted, marginTop: 14 }}
      >
        {noLibrary ? NO_STRATEGY_LIBRARY : NO_STRATEGIES}
      </p>
    )
  }

  /**
   * Whether anything on this list carries a figure at all.
   *
   * On the fallback path nothing does, `indicativeTotalKw` is 0, and a "0.0 kW"
   * total under a list of unquantified rows would be a figure asserting
   * something the rows have just said they cannot assert.
   */
  const quantified = ranked.some((entry) => entry.notApplicable === null)

  /**
   * The reason shared by every row, when there is one — else null.
   *
   * On the fallback path all three of Sunrise Elementary's strategies come back
   * with the SAME sentence, and printing it under each row says one fact three
   * times while reading as though three separate things went wrong. It is a
   * property of the account, not of the strategy, so it belongs above the list
   * once.
   *
   * Only hoisted when it is genuinely shared: a list mixing "no decomposition"
   * with "this end use is negligible here" must keep each row's own reason, or
   * the one sentence shown would be false of some of the rows.
   */
  const sharedReason = !quantified
    && ranked.every((entry) => entry.notApplicable?.reason === ranked[0]?.notApplicable?.reason)
    ? ranked[0]?.notApplicable?.reason ?? null
    : null

  return (
    <>
      {sharedReason === null
        ? null
        : (
          <p
            data-testid="not-applicable-reason"
            style={{ fontSize: 14, color: T.color.muted, margin: '12px 0 0' }}
          >
            {sharedReason}
          </p>
        )}
      <ol data-testid="strategy-rows" style={{ listStyle: 'none', margin: '14px 0 0', padding: 0 }}>
        {ranked.map((entry) => (
          <Row
            key={entry.strategy.id}
            entry={entry}
            all={ranked}
            rejected={rejected[entry.strategy.id]}
            onReject={onReject}
            reasonHoisted={sharedReason !== null}
          />
        ))}
      </ol>
      {showAggregate && quantified ? <Aggregate aggregate={aggregate} /> : null}
    </>
  )
}

function Row(
  { entry, all, rejected, onReject, reasonHoisted }: {
    entry: RankedStrategy
    all: RankedStrategy[]
    rejected: Rejected | undefined
    onReject?: ((strategyId: string) => void) | undefined
    /** True when every row shares one reason and the list states it once above. */
    reasonHoisted: boolean
  },
): JSX.Element {
  const { strategy, notApplicable, statedPrerequisites } = entry
  const overlaps = notApplicable === null ? overlapsWith(entry, all) : []

  return (
    <li
      data-testid="strategy-row"
      data-strategy={strategy.id}
      // The fallback path, readable without a computed style. `false` is the
      // strategy that applies to this sector but has no load to act on; it is
      // NOT the empty list and NOT an error.
      data-quantified={notApplicable === null ? 'true' : 'false'}
      data-rejected={rejected !== undefined && !rejected.failed ? 'true' : 'false'}
      style={{
        borderTop: `1px solid ${T.color.hairline}`,
        padding: '12px 0',
        opacity: rejected !== undefined && !rejected.failed ? 0.72 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'baseline' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: T.weight.semi, color: T.color.ink }}>
            {strategy.name}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            <span data-testid="strategy-timing" style={microLabel}>{timingWords(strategy.timing)}</span>
            <ImpactPill impact={strategy.impact} />
          </div>
        </div>
        <div style={{ flex: 'none', textAlign: 'right' }}>
          {notApplicable === null
            ? <IndicativeKw kw={entry.indicativeKw} />
            : (
              /*
                Requirement: the fallback path renders as UNQUANTIFIED, not as
                zero and not as empty. No `IndicativeKw` here at all — a
                `0.0 kW indicative` would be a figure, and the reason this row
                exists is that there is no figure to give.
              */
              <span
                data-testid="not-quantified"
                style={{ ...microLabel, color: T.color.undetermined }}
              >
                Not quantified here
              </span>
            )}
        </div>
      </div>

      {/* The strategy's OWN framing sentence, from the library. Not R-33's. */}
      <p style={{ fontSize: 14.5, lineHeight: 1.6, color: T.color.body, margin: '8px 0 0' }}>
        {strategy.framingStatement}
      </p>

      {notApplicable === null || reasonHoisted
        ? null
        : (
          <p
            data-testid="not-applicable-reason"
            style={{ fontSize: 14, color: T.color.muted, margin: '6px 0 0' }}
          >
            {notApplicable.reason}
          </p>
        )}

      {statedPrerequisites.length === 0
        ? null
        : (
          <div style={{ marginTop: 8 }}>
            <span style={microLabel}>{NEEDS_LABEL}</span>
            <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 14, lineHeight: 1.55, color: T.color.body }}>
              {statedPrerequisites.map((prerequisite) => (
                /*
                  One prerequisite per element, deliberately: a rep reads them
                  as a list of questions to ask, and a test asserting the text
                  of one must not have to match a sentence built around it.
                */
                <li key={prerequisite} data-testid="prerequisite">{prerequisite}</li>
              ))}
            </ul>
          </div>
        )}

      {overlaps.length === 0
        ? null
        : (
          <p
            data-testid="strategy-overlap"
            style={{ fontSize: 12.5, color: T.color.muted, margin: '8px 0 0' }}
          >
            {`Acts on the same load as ${overlaps.join(' and ')}, so these do not add.`}
          </p>
        )}

      {onReject === undefined
        ? null
        : (
          <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="reject-strategy"
              disabled={rejected !== undefined && !rejected.failed}
              onClick={() => { onReject(entry.strategy.id) }}
              style={{
                ...quietButton,
                cursor: rejected !== undefined && !rejected.failed ? 'default' : 'pointer',
              }}
            >
              Not available
            </button>
            {rejected === undefined
              ? null
              : rejected.failed
                ? (
                  <span role="alert" style={{ fontSize: 12.5, color: T.color.red }}>
                    That rejection could not be recorded.
                  </span>
                )
                : (
                  /*
                    `rejection-recorded`, not `exclusion-offered`. This span and
                    `AccountDetail.tsx`'s `exclusion-offer` banner render into
                    the same document, and the two ids differed by a trailing
                    `d` — a typo in either direction would have found a real
                    element and asserted about the wrong one, silently. They are
                    also different things: the banner is the actionable offer
                    with the button on it, and this is the in-row confirmation
                    that the rejection was written down.
                  */
                  <span data-testid="rejection-recorded" style={{ fontSize: 12.5, color: T.color.muted }}>
                    {`${OFFER_PREFIX} ${rejected.endUses.map((endUse) => END_USE_WORDS[endUse]).join(', ')}. `
                      + 'Apply it in the what-if when the customer is ready.'}
                  </span>
                )}
          </div>
        )}
    </li>
  )
}

/**
 * The only total on this surface, and the only one permitted (R-33).
 *
 * It is `aggregateStrategies`' output, transported whole. Nothing in this
 * package adds `indicativeKw` across rows — the figure above the fold is
 * overlap-adjusted (two strategies that both shed cooling do not shed it twice)
 * and capped at the metered nomination, and re-deriving it here from the rows
 * would be exactly the arithmetic the customer is about to do and the reason
 * the engine function exists.
 *
 * **The note under it is not conditional on `capped`.** A note that appeared
 * only when the cap bound would leave the uncapped case looking like a plain
 * sum — the one reading this surface must never invite — so the rule is always
 * stated and the note says additionally when the cap actually bound.
 *
 * ## Why it has a ground of its own, and why that ground is `rescore`
 *
 * It was a plain block under a 1.5px rule and it read as one more row of the
 * list — which is the one thing it is not. A tinted, padded block at
 * `AGGREGATE_KW_SIZE` says "this is the summary of the section" before a word
 * is read.
 *
 * **`T.tint.rescore` (#eceae7), and the alternatives are each rejected for a
 * reason.** `blue` is the metered nomination's own KPI ground — 44px navy on
 * `tint.blue`, twelve hundred pixels up the same page — and this figure must
 * never be mistaken for it (`IndicativeKw.tsx`). `green` is the season-value
 * KPI's ground on that same screen, so it reads as a third KPI. `enrolled`,
 * `stale` and `red` are the three `ImpactPill` grounds above, and a summary
 * joining the low/medium/high scale would be claiming an impact it has not
 * computed. `rescore` is the one warm neutral in the palette: a ground, not a
 * status, and its only other use is a disabled date input on another surface,
 * where nothing about a greyed 12.5px text field is confusable with this.
 *
 * The 1.5px rule stays on top of it. It is what separates the summary from the
 * last row, and the design system's stated rule is that borders do the work
 * shadows normally would — the tint is an addition to that, not a replacement.
 */
function Aggregate({ aggregate }: { aggregate: AccountRanking['aggregate'] }): JSX.Element {
  return (
    <div
      data-testid="aggregate"
      style={{
        borderTop: `1.5px solid ${T.color.rule}`,
        background: T.tint.rescore,
        marginTop: 14,
        padding: '14px 18px',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 20,
        alignItems: 'baseline',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div style={microLabel}>Together, at most</div>
        <div style={{ marginTop: 4 }}>
          <IndicativeKw
            kw={aggregate.indicativeTotalKw}
            testId="aggregate-kw"
            size={AGGREGATE_KW_SIZE}
          />
        </div>
      </div>
      <p
        data-testid="capped-note"
        data-capped={aggregate.capped ? 'true' : 'false'}
        style={{ fontSize: 12.5, color: T.color.muted, margin: 0, maxWidth: '52ch', textAlign: 'right' }}
      >
        {aggregate.capped
          ? 'Overlap-adjusted and capped at what this account’s own meter data supports — '
            + 'the cap bound here, so this is less than the figures above suggest. Never a sum of them.'
          : 'Overlap-adjusted and capped at what this account’s own meter data supports. '
            + 'Never a sum of the figures above.'}
      </p>
    </div>
  )
}
